/**
 * 33 - Power Station (10 §2): three breaker panels in a fenced switch yard inside the station's wire, each
 * worked with Interact for three seconds. Each breaker feeds a third of the map's searchlights and automated
 * mini-bunkers (south, middle, north): throw it and they go dead for good - the mission gets easier as it
 * succeeds. Soft quiet rule: a full alarm posts two elites in the yard.
 */

import { buildColumn } from '../compound';
import { placeMiniBunker } from '../garrison';
import { FLAG } from '../grid';
import { buildRun } from '../obstacles';
import type { BlackoutGroupDef, MissionDef } from '../../sim/types';
import { checker, clutter, guard, guardAtDoor, guardsAbout, insideRect, ruleOf, structure } from './helpers';
import { dress, lamp, put } from './helpers3a';
import { checkBlackouts, checkTargets } from './helpers3aAsserts';
import type { ArchetypeSpec } from './types';

export const BREAKERS = 3;
const HOLD_SECONDS = 3;
/** The station's own lamps - one per breaker at least, so no switch is ever dead - and guns from threat 6. */
export const lampsFor = (threat: number): number => (threat >= 5 ? 4 : 3);
export const bunkersFor = (threat: number): number => (threat >= 8 ? 2 : threat >= 6 ? 1 : 0);

/** Share things out among the breakers by where they stand: the southernmost third on the first, and so on north. */
function thirds<T>(items: readonly T[], z: (item: T) => number): T[][] {
  const sorted = [...items].sort((a, b) => z(b) - z(a));
  return Array.from({ length: BREAKERS }, (_, i) =>
    sorted.slice(Math.round((i * sorted.length) / BREAKERS), Math.round(((i + 1) * sorted.length) / BREAKERS)));
}

/** Wire every searchlight and every automated gun on the finished map to a breaker. */
function wire(def: MissionDef, groups: BlackoutGroupDef[]): void {
  const lamps = thirds(def.props.filter((p) => p.kind === 'searchlight'), (p) => p.z);
  const machines = def.spawns.map((s, index) => ({ s, index }))
    .filter(({ s }) => s.kind === 'miniBunker' || s.kind === 'minitank');
  const guns = thirds(machines, (m) => m.s.z);
  groups.forEach((group, i) => {
    group.searchlightPropIds = (lamps[i] ?? []).map((p) => p.id);
    group.spawnIndexes = (guns[i] ?? []).map((m) => m.index);
  });
}

export const powerStation: ArchetypeSpec = {
  id: 'power-station',
  name: 'POWER STATION',
  nativeBiome: 'temperate',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [52, 58], d: [34, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    const cx = Math.round((g.rect.x0 + g.rect.x1) / 2);
    const cz = Math.round((g.rect.z0 + g.rect.z1) / 2);
    // The switch yard: a wire pen in the north half, its gate in the middle of its south side.
    const yard = { x0: cx - 12, z0: cz - 12, x1: cx + 12, z1: cz - 1 };
    buildRun(g.map, 'wire', yard.z1, yard.x0, yard.x1, [{ x: cx, width: 4.5 }]);
    buildRun(g.map, 'wire', yard.z0, yard.x0, yard.x1, []);
    for (const x of [yard.x0, yard.x1]) buildColumn(g.map, 'wire', x, yard.z0 + 0.7, yard.z1 - 0.7, []);
    g.map.mask.circle(cx, yard.z1, 2.5, FLAG.keep);

    // Three transformers along the back of the pen, the panel that isolates each in front of it.
    const panels = [-1, 0, 1].map((i) => {
      dress(g, 'generatorHut', cx + i * 7.5, yard.z0 + 3, { margin: 0.4 });
      // A clear metre from the transformer's wall, and room to stand at it on the gate's side.
      return put(g, 'breakerPanel', cx + i * 7.5, yard.z1 - 4, { target: true, margin: 0.3 });
    });

    // The corners and the yard's gate first, then wherever else along the wire there is room: the road, the side
    // gate and the corridor to the pickup each take a corner on some maps, and every breaker needs its lamp.
    const lamps = lampsFor(g.threat);
    const spots = [
      [g.inner.x0 + 2, g.inner.z1 - 2], [g.inner.x1 - 2, g.inner.z1 - 2], [cx + 9, yard.z1 + 3], [cx - 9, yard.z1 + 3],
      [g.inner.x0 + 2, g.inner.z0 + 2], [g.inner.x1 - 2, g.inner.z0 + 2],
      [cx + 16, yard.z1 + 6], [cx - 16, yard.z1 + 6],
    ] as const;
    let lit = 0;
    for (const [x, z] of spots) if (lit < lamps && lamp(g, x, z)) lit++;
    if (lit < BREAKERS) g.map.problems.push('power-station: no room for the station searchlights');
    let guns = 0;
    for (let tries = 0; tries < 40 && guns < bunkersFor(g.threat); tries++) {
      const x = cx + g.rng.range(-14, 14);
      const z = yard.z1 + g.rng.range(4, 9);
      if (insideRect(g.inner, x, z) && placeMiniBunker(g.map, x, z, 1)) guns++;
    }

    // The turbine hall and the control room outside the pen.
    const south = { ...g.inner, z0: yard.z1 + 2 };
    const hall = structure(g, 'hut', { within: south, margin: 1.2 });
    structure(g, 'guardHouse', { within: south, margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (hall) guardAtDoor(g, hall);
    guard(g, cx + 3, yard.z1 + 2, 0);
    guardsAbout(g, 1 + g.rng.int(2));

    const groups = panels.map(
      (p): BlackoutGroupDef => ({ switchPropId: p.id, searchlightPropIds: [], spawnIndexes: [] }),
    );
    return {
      objective: {
        kind: 'useObjects', label: 'THROW THE BREAKERS', targetPropIds: panels.map((p) => p.id), minFraction: 1,
        holdSeconds: HOLD_SECONDS, useLabel: 'THROW THE BREAKER',
        rules: [
          { type: 'alertConsequence', level: 3, delaySeconds: 0, effect: 'reinforceTarget' },
          { type: 'recall', mode: 'normal' },
        ],
      },
      mission: { blackoutGroups: groups },
      // Last of all, so the lamps and the mini-bunkers the budget bought are on the breakers too.
      finish: (def) => wire(def, def.blackoutGroups ?? []),
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('power-station');
    const panels = checkTargets(check, def, ['breakerPanel']);
    check(panels.length === BREAKERS, `needs ${BREAKERS} breaker panels, has ${panels.length}`);
    check(
      def.objective.kind === 'useObjects' && def.objective.minFraction === 1
        && def.objective.holdSeconds === HOLD_SECONDS,
      `every breaker must be thrown, ${HOLD_SECONDS} s of work each`,
    );
    check((def.objective.useLabel ?? '').length > 0, 'the Interact prompt needs its words');
    check(ruleOf(def, 'alertConsequence')?.effect === 'reinforceTarget', 'a full alarm must reinforce the switch yard');
    for (const p of panels) {
      check(insideRect(def.goalZone, p.x, p.z), `breaker ${p.id} stands outside the station`);
      check(probe.breach(p.id) === 0, `breaker ${p.id} is fenced in: the yard's gate is shut`);
    }
    // The pen: wire on the far side of every panel from the gate, and the panels in one row.
    check(panels.every((p) => p.z === panels[0]?.z), 'the breakers stand in one row');
    const fenced = panels.every((p) => def.props.some((w) => w.kind === 'wire' && w.z < p.z && w.z > p.z - 9
      && Math.abs(w.x - p.x) <= (w.length ?? 0) / 2 + 0.5));
    check(fenced, 'the breakers must stand in a fenced yard');
    checkBlackouts(check, def, panels);
    const groups = def.blackoutGroups ?? [];
    check(
      groups.every((group) => group.searchlightPropIds.length >= 1),
      'every breaker must darken at least one searchlight',
    );
    const sizes = groups.map((group) => group.searchlightPropIds.length);
    check(Math.max(...sizes, 0) - Math.min(...sizes, 99) <= 1, 'the searchlights are shared out in thirds');
    const machines = def.spawns.filter((s) => s.kind === 'miniBunker' || s.kind === 'minitank').length;
    check(
      groups.reduce((n, group) => n + group.spawnIndexes.length, 0) === machines,
      'every mini-bunker hangs on a breaker',
    );
    return out;
  },
};
